import { Suspense, lazy, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import Skeleton from "../components/states/Skeleton";
import { isStaff, useAuth } from "../lib/auth";
import { parseManifest, type DashboardManifest } from "../lib/manifest";
import { visibleTabIds } from "../lib/navSections.mjs";
import { SETTINGS_TABS, settingsTabFromPath, settingsTabPath } from "../lib/settingsNav.mjs";
import {
  useUserPreferences,
  type NotificationToggles,
  type UserPreferences,
} from "../lib/userPreferences";
import { useEffect } from "react";

// Settings > Inputs is its own chunk: it carries the report libs the inputs
// are saved through, which the Preferences tab and the rest of the shell do
// not need.
const InputsTab = lazy(() => import("./settings/InputsTab"));

/**
 * MER-164: Settings page v1.
 *
 * Four sections per the spec:
 *   - Profile (email read-only, full name editable, customer, tier)
 *   - Security (change password + MFA links to Supabase hosted flows)
 *   - Notifications (4 toggles, opt-in)
 *   - Dashboard preferences (default landing tab, default period)
 *
 * Persistence is localStorage for v1 (filed as MER-XXX follow-up to
 * migrate to a Supabase `user_preferences` table once multi-device
 * sync is a stated need).
 *
 * Default landing tab + default period are read on CustomerDashboard
 * mount: an empty `?period=` URL inherits the saved value; the index
 * redirect picks `defaultLandingTab` when set and visible.
 *
 * MER-688: the landing-tab options come from visibleTabIds, the same
 * gate the sidebar uses, so a client never sees a staff_only tab named
 * here (Benchmarking, Financial Health, Sales Pipeline, Data Quality) and
 * cannot pick one. CustomerDashboard resolves the saved value through the
 * same helper, so a stale preference falls back to the first visible tab.
 *
 * Two tabs since 2026-10-01 (lib/settingsNav.mjs names them and their URLs):
 *   Preferences  /<slug>/Settings          this page as it was, per browser
 *   Inputs       /<slug>/Settings/Inputs   every number the customer types
 *                in, grouped by report, stored per tenant in Postgres
 *                (pages/settings/InputsTab.tsx)
 * The route is /<slug>/Settings/* in CustomerDashboard; the tab is read off
 * the path, so a link or a reload lands on the tab it names.
 */
export default function Settings() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const tab = settingsTabFromPath(location.pathname, slug);
  const { session } = useAuth();
  const userId = session?.user.id ?? null;
  const email = session?.user.email ?? "";
  const { prefs, setPrefs } = useUserPreferences(userId, slug);

  const [draft, setDraft] = useState<UserPreferences>(prefs);
  const [saveState, setSaveState] = useState<"idle" | "saved">("idle");
  useEffect(() => {
    setDraft(prefs);
  }, [prefs]);

  const [manifest, setManifest] = useState<DashboardManifest | null>(null);
  useEffect(() => {
    if (!slug) return;
    fetch(`/manifests/${slug}.yaml`)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((text) => setManifest(parseManifest(text)))
      .catch(() => setManifest(null));
  }, [slug]);

  const tabIds = useMemo(
    () => (manifest ? visibleTabIds(manifest.tabs, { isStaff: isStaff(session) }) : []),
    [manifest, session],
  );
  const tier = manifest?.tier ?? "growth";
  const customerName = manifest?.customer_name ?? slug ?? "";

  function update<K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setSaveState("idle");
  }

  function updateNotification(key: keyof NotificationToggles, value: boolean) {
    setDraft((prev) => ({
      ...prev,
      notifications: { ...prev.notifications, [key]: value },
    }));
    setSaveState("idle");
  }

  function handleSave() {
    setPrefs(draft);
    setSaveState("saved");
  }

  const tabs = (
    <nav
      aria-label="Settings sections"
      style={{
        display: "flex",
        gap: 4,
        marginBottom: 20,
        borderBottom: "1px solid var(--border-subtle)",
      }}
    >
      {SETTINGS_TABS.map((t) => (
        <Link
          key={t.id}
          to={settingsTabPath(slug, t.id)}
          aria-current={tab === t.id ? "page" : undefined}
          style={{
            padding: "8px 14px",
            marginBottom: -1,
            fontSize: 13,
            fontWeight: tab === t.id ? 700 : 500,
            color: tab === t.id ? "var(--brand-deep)" : "var(--text-2)",
            borderBottom: tab === t.id ? "2px solid var(--brand-teal)" : "2px solid transparent",
            textDecoration: "none",
          }}
        >
          {t.label}
        </Link>
      ))}
    </nav>
  );

  if (tab === "inputs") {
    return (
      <main style={{ padding: "24px 32px", maxWidth: 1120 }}>
        <header style={{ marginBottom: 16 }}>
          <h1 style={{ fontSize: 22, fontWeight: 600, color: "var(--text)" }}>Settings</h1>
        </header>
        {tabs}
        <Suspense fallback={<Skeleton label="Loading inputs" height={120} />}>
          <InputsTab manifest={manifest} slug={slug} />
        </Suspense>
      </main>
    );
  }

  return (
    <main style={{ padding: "24px 32px", maxWidth: 720 }}>
      <header style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, color: "var(--text)" }}>Settings</h1>
      </header>
      {tabs}
      <p style={{ marginBottom: 20, fontSize: 13, color: "var(--text-muted)" }}>
        Preferences are saved to this browser. Multi-device sync is on the roadmap.
      </p>

      <Section title="Profile">
        <ReadOnlyRow label="Email" value={email || "(not signed in)"} />
        <EditableRow
          label="Full name"
          value={draft.fullName}
          placeholder="Your name"
          onChange={(v) => update("fullName", v)}
        />
        <ReadOnlyRow label="Customer" value={customerName} />
        <ReadOnlyRow label="Tier" value={tier.toUpperCase()} pill />
      </Section>

      <Section title="Security">
        <LinkRow
          label="Change password"
          description="We'll email a reset link to your address."
          href="/forgot-password"
        />
        <LinkRow
          label="Manage MFA"
          description="Two-factor authentication is managed in Supabase. Open the hosted MFA page to enroll or rotate."
          href="https://supabase.com/dashboard/account/security"
          external
        />
      </Section>

      <Section title="Notifications">
        <p
          style={{
            fontSize: 12,
            color: "var(--text-muted)",
            marginTop: -8,
            marginBottom: 12,
          }}
        >
          Email me when:
        </p>
        <Toggle
          label="Data freshness alerts"
          description="When a configured source goes stale past its expected window."
          checked={draft.notifications.freshness}
          onChange={(v) => updateNotification("freshness", v)}
        />
        <Toggle
          label="RED flag critical alerts"
          description="When a new RED flag is raised on any KPI."
          checked={draft.notifications.redFlag}
          onChange={(v) => updateNotification("redFlag", v)}
        />
        <Toggle
          label="Sync failures"
          description="When the 4h scheduler tick fails for any stage."
          checked={draft.notifications.syncFailure}
          onChange={(v) => updateNotification("syncFailure", v)}
        />
        <Toggle
          label="Weekly digest"
          description="Mondays at 8am local: a one-page snapshot of last week's KPIs."
          checked={draft.notifications.weeklyDigest}
          onChange={(v) => updateNotification("weeklyDigest", v)}
        />
      </Section>

      <Section title="Dashboard preferences">
        <SelectRow
          label="Default landing tab"
          description="The tab that opens when you sign in or click the customer logo."
          value={draft.defaultLandingTab ?? ""}
          options={[{ value: "", label: "First visible tab (default)" }].concat(
            tabIds.map((id) => ({ value: id, label: id })),
          )}
          onChange={(v) => update("defaultLandingTab", v || null)}
        />
        <SelectRow
          label="Default period type"
          description="Pre-selects the global period filter when a tab opens. Default lets each tile use its own grain; picking a period forces it onto every tile."
          value={draft.defaultPeriod ?? ""}
          options={[
            // MER-497: "" (no saved pref) and "monthly" are no longer
            // equivalent. Empty = the Default selector state, each tile
            // keeps its declared grain; "monthly" is an explicit override
            // that forces monthly grain (complete-month windows) on every
            // period-aware tile.
            { value: "", label: "Default (per-tile grain)" },
            { value: "monthly", label: "Monthly (force)" },
            { value: "weekly", label: "Weekly (force)" },
            { value: "daily", label: "Daily (force)" },
          ]}
          onChange={(v) => update("defaultPeriod", (v as UserPreferences["defaultPeriod"]) || null)}
        />
      </Section>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginTop: 8,
        }}
      >
        <button type="button" onClick={handleSave} style={primaryButtonStyle}>
          Save changes
        </button>
        {saveState === "saved" ? (
          <span style={{ fontSize: 13, color: "var(--brand-deep)" }}>
            Saved. Reload the dashboard to see preferences applied.
          </span>
        ) : null}
      </div>
    </main>
  );
}

// ──────────────────────────────────────────────────────────────────
// Tiny presentational helpers. Pulled out so Settings.tsx scans as a
// flat list of sections + rows rather than nested JSX.
// ──────────────────────────────────────────────────────────────────

function Section(props: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: 12,
        padding: "20px 22px",
        marginBottom: 16,
      }}
    >
      <h2
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: "var(--text)",
          textTransform: "uppercase",
          letterSpacing: 0.6,
          marginBottom: 14,
        }}
      >
        {props.title}
      </h2>
      <div style={{ display: "grid", gap: 12 }}>{props.children}</div>
    </section>
  );
}

function ReadOnlyRow(props: { label: string; value: string; pill?: boolean }) {
  return (
    <Row label={props.label}>
      {props.pill ? (
        <span
          style={{
            display: "inline-block",
            fontSize: 11,
            padding: "4px 10px",
            borderRadius: 12,
            background:
              "linear-gradient(95deg, rgba(15,107,92,0.08) 0%, rgba(38,168,159,0.08) 100%)",
            border: "1px solid var(--brand-line)",
            color: "var(--brand-deep)",
            textTransform: "uppercase",
            letterSpacing: 0.4,
            fontWeight: 700,
          }}
        >
          {props.value}
        </span>
      ) : (
        <span style={{ fontSize: 14, color: "var(--text)" }}>{props.value}</span>
      )}
    </Row>
  );
}

function EditableRow(props: {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  return (
    <Row label={props.label}>
      <input
        type="text"
        value={props.value}
        placeholder={props.placeholder}
        onChange={(ev) => props.onChange(ev.target.value)}
        style={inputStyle}
      />
    </Row>
  );
}

function SelectRow(props: {
  label: string;
  description?: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <Row label={props.label} description={props.description}>
      <select
        value={props.value}
        onChange={(ev) => props.onChange(ev.target.value)}
        style={inputStyle}
      >
        {props.options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </Row>
  );
}

function LinkRow(props: { label: string; description: string; href: string; external?: boolean }) {
  return (
    <Row label={props.label} description={props.description}>
      <a
        href={props.href}
        target={props.external ? "_blank" : undefined}
        rel={props.external ? "noopener noreferrer" : undefined}
        style={{ color: "var(--brand-deep)", fontWeight: 600, fontSize: 13 }}
      >
        Open
        {props.external ? " (new tab)" : ""}
      </a>
    </Row>
  );
}

function Toggle(props: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <Row label={props.label} description={props.description}>
      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={props.checked}
          onChange={(ev) => props.onChange(ev.target.checked)}
        />
        <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
          {props.checked ? "Enabled" : "Disabled"}
        </span>
      </label>
    </Row>
  );
}

function Row(props: { label: string; description?: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1.2fr",
        gap: 18,
        alignItems: "start",
        paddingBottom: 4,
      }}
    >
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{props.label}</div>
        {props.description ? (
          <div
            style={{
              fontSize: 11,
              color: "var(--text-muted)",
              marginTop: 4,
              lineHeight: 1.45,
            }}
          >
            {props.description}
          </div>
        ) : null}
      </div>
      <div>{props.children}</div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  font: "inherit",
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--border-subtle)",
  background: "var(--bg-elevated)",
  color: "var(--text-primary)",
  fontSize: 13,
  width: "100%",
};

const primaryButtonStyle: React.CSSProperties = {
  font: "inherit",
  fontWeight: 600,
  padding: "10px 18px",
  borderRadius: 10,
  border: "none",
  background: "linear-gradient(95deg, var(--brand-deep) 0%, var(--brand-teal) 100%)",
  color: "white",
  cursor: "pointer",
  letterSpacing: 0.3,
};
